import { ENUM_ECO_CART_EVENT, ItfEcoCart, ItfEcoCartIsLogin, ItfEcoCartItem, ItfEcoCartItemChange, ItfEcoCartItemInit } from './type.d';
import EcoDesignEventsBus from 'eco-design-micro/EcoDesignEventsBus';
import { MAX_CART_NUM, MIN_CART_NUM } from './const';
import { ItfCouponGift, ItfCouponGiftProductItem, ItfEcoCartCouponItem } from './comps/EcoCartCoupon/type.d';
import * as SERVICE from './service';
import * as STORAGE from './localstorage';
import { getIsLogin } from 'eco-design-reliance/getIsLogin';
import { handle_GTM_29 } from 'eco-design-micro/gtmEvent/29';
import { handle_GTM_30 } from 'eco-design-micro/gtmEvent/30';

class EcoCartGlobalStore {
    private static instance: EcoCartGlobalStore;
    private cart: ItfEcoCart = { open: false, items: [], coupon: null, isLogin: -1, icon: '' };
    private subscribers: Set<(cart: ItfEcoCart) => void> = new Set();
    private isInitialized = false;

    // 私有构造函数，确保单例
    private constructor() {}

    static getInstance(): EcoCartGlobalStore {
        if (!EcoCartGlobalStore.instance) {
            EcoCartGlobalStore.instance = new EcoCartGlobalStore();
        }
        return EcoCartGlobalStore.instance;
    }

    // 初始化事件监听（只执行一次）
    init() {
        if (this.isInitialized) {
            return;
        }

        console.log('EcoCartGlobalStore: Initializing event listeners');
        this.isInitialized = true;

        this.initVisibilityListener();

        EcoDesignEventsBus.on(ENUM_ECO_CART_EVENT.ECO_CART_OPEN, this.handleCartOpen.bind(this)); // 购物车 - 开/闭
        EcoDesignEventsBus.on(ENUM_ECO_CART_EVENT.ECO_CART_QUERY, this.handleCartQuery.bind(this)); // 购物车 - 初始化
        EcoDesignEventsBus.on(ENUM_ECO_CART_EVENT.ECO_CART_ADD, this.handleCartAdd.bind(this)); // 页面中 - 按钮添加
        EcoDesignEventsBus.on(ENUM_ECO_CART_EVENT.ECO_CART_INCREASE, this.handleCartIncrease.bind(this)); // 购物车中 - 增
        EcoDesignEventsBus.on(ENUM_ECO_CART_EVENT.ECO_CART_DECREASE, this.handleCartDecrease.bind(this)); // 购物车中 - 减
        EcoDesignEventsBus.on(ENUM_ECO_CART_EVENT.ECO_CART_REMOVE, this.handleCartRemove.bind(this)); // 购物车中 - 删
        EcoDesignEventsBus.on(ENUM_ECO_CART_EVENT.ECO_CART_MERGE, this.handleCartMerge.bind(this)); // 登录时 - 合并购物车
        EcoDesignEventsBus.on(ENUM_ECO_CART_EVENT.ECO_CART_ISLOGIN, this.handleCartIsLogin.bind(this)); // 登录状态
        EcoDesignEventsBus.on(ENUM_ECO_CART_EVENT.ECO_CART_COUPON, this.handleCartCoupon.bind(this)); // coupon/gift card
        EcoDesignEventsBus.on(ENUM_ECO_CART_EVENT.ECO_CART_ICON, this.handleCartIcon.bind(this)); // 针对配置化组件 - 组件存在则挂件中出现购物车图标
    }
    // 初始化页面可见性监听
    private initVisibilityListener() {
        if (typeof window !== 'undefined') {
            // 监听页面可见性变化
            document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));

            // 如果支持 Page Visibility API
            if (typeof document.hidden !== 'undefined') {
                document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
            } else {
                window.addEventListener('focus', this.handleWindowFocus.bind(this));
            }
        }
    }

    private handleVisibilityChange() {
        if (window.document.visibilityState === 'visible') {
            this.refreshCartData();
        }
    }

    private handleWindowFocus() {
        this.refreshCartData();
    }

    private async refreshCartData() {
        const isLogin = getIsLogin() ? 1 : 0;
        this.cart.isLogin = isLogin;
        const locale = window?.location?.pathname?.split('/')?.[1] || 'us';
        const items: ItfEcoCartItem[] = await this.handleCartQuery(locale);
        if (isLogin === 1 && items?.length > 0) {
            this.handleCartCoupon(items, isLogin, locale);
        }
        this.notifySubscribers();
    }

    // 事件处理函数
    private async handleCartOpen(open: boolean) {
        console.log('handleCartOpen:', open);
        this.cart.open = open;
        this.notifySubscribers();
    }

    private async handleCartQuery(locale: string): Promise<ItfEcoCartItem[]> {
        console.log('handleCartQuery:', locale);
        if (!locale) return [] as ItfEcoCartItem[];
        const storageCarts: ItfEcoCartItem[] = STORAGE.STORAGE_GET_CARTS();
        if (this.cart.isLogin === 1) {
            const data: ItfEcoCartItemInit[] = await SERVICE.API_PAGE_USER_CART(locale);
            const carts: ItfEcoCartItem[] = data?.map((item: ItfEcoCartItemInit) => ({
                variantId: item.variantId,
                productId: item.productId,
                price: Number(item.price),
                compareAtPrice: Number(item.compareAtPrice),
                variantImg: item.variantImg,
                img: item.img,
                inventoryQuantity: item.inventoryQuantity,
                inventoryPolicy: item.inventoryPolicy,
                skuTitle: item.skuTitle,
                productTitle: item.productTitle,
                quantity: item.quantity,
            }));
            this.cart.items = carts;
            this.notifySubscribers();
            return carts;
        } else if (this.cart.isLogin === 0) {
            this.cart.items = storageCarts;
            this.notifySubscribers();
            return storageCarts;
        }
        return [] as ItfEcoCartItem[];
    }

    private async handleCartMerge(locale: string): Promise<ItfEcoCartItem[]> {
        console.log('handleCartMerge:', locale);
        if (!locale) return [] as ItfEcoCartItem[];
        const storageCarts: ItfEcoCartItem[] = STORAGE.STORAGE_GET_CARTS();
        const productList: ItfEcoCartItemChange[] = storageCarts?.map((cart: ItfEcoCartItem) => ({
            compareAtPrice: cart.compareAtPrice,
            price: cart.price,
            productId: Number(cart.productId),
            variantId: Number(cart.variantId),
            quantity: cart.quantity,
        }));
        const data: ItfEcoCartItemInit[] = await SERVICE.API_MERGE_USER_CART({ productList }, locale);
        const carts: ItfEcoCartItem[] = data?.map((item: ItfEcoCartItemInit) => ({
            variantId: item.variantId,
            productId: item.productId,
            price: Number(item.price),
            compareAtPrice: Number(item.compareAtPrice),
            variantImg: item.variantImg,
            img: item.img,
            inventoryQuantity: item.inventoryQuantity,
            inventoryPolicy: item.inventoryPolicy,
            skuTitle: item.skuTitle,
            productTitle: item.productTitle,
            quantity: item.quantity,
        }));
        this.cart.items = carts;
        this.notifySubscribers();
        STORAGE.STORAGE_REMOVE_CARTS();
        return carts;
    }

    private async handleCartAdd(data: ItfEcoCartItem[]) {
        console.log('handleCartAdd:', data);
        if (!Array.isArray(data)) return;
        const items: ItfEcoCartItem[] = data?.filter((i: ItfEcoCartItem) => i.quantity > 0);
        if (items?.length <= 0) return;
        items?.forEach((x: ItfEcoCartItem) => {
            const ext = this.cart.items?.find((y: ItfEcoCartItem) => x.variantId === y.variantId);
            ext ? (ext.quantity += x.quantity) : this.cart.items.unshift(x);
        });
        this.cart.open = true;
        this.notifySubscribers();

        if (this.cart.isLogin === 1) {
            const productList: ItfEcoCartItemChange[] = items.map((item: ItfEcoCartItem) => ({
                compareAtPrice: item.compareAtPrice,
                price: item.price,
                productId: Number(item.productId),
                variantId: Number(item.variantId),
                quantity: item.quantity,
            }));
            await SERVICE.API_INCREASE_USER_CART({ productList });
        } else if (this.cart.isLogin === 0) {
            STORAGE.STORAGE_INCREASE_CARTS(items);
        }
    }

    private async handleCartIncrease(item: ItfEcoCartItem) {
        console.log('handleCartIncrease:', item);
        if (item.quantity >= (item.inventoryPolicy === 'continue' ? MAX_CART_NUM : Math.min(MAX_CART_NUM, item.inventoryQuantity)) || !item.variantId) return;

        const ext = this.cart.items?.find((x: ItfEcoCartItem) => x.variantId === item.variantId);
        if (ext && ext.quantity < MAX_CART_NUM) {
            ext.quantity += 1;
        }
        this.cart.open = true;
        this.notifySubscribers();

        if (this.cart.isLogin === 1) {
            const productList: ItfEcoCartItemChange[] = [
                {
                    compareAtPrice: item.compareAtPrice,
                    price: item.price,
                    productId: Number(item.productId),
                    variantId: Number(item.variantId),
                    quantity: 1,
                },
            ];
            await SERVICE.API_INCREASE_USER_CART({ productList });
        } else if (this.cart.isLogin === 0) {
            STORAGE.STORAGE_INCREASE_CARTS([{ ...item, quantity: 1 }]);
        }

        handle_GTM_29({
            product_name: item.productTitle || item.skuTitle,
            price: item.price,
            quantity: 1,
            button_name: '+',
            product_id: item.productId,
            variant_id: item.variantId,
        });
    }

    private async handleCartDecrease(item: ItfEcoCartItem) {
        console.log('handleCartDecrease:', item);
        if (item.quantity <= MIN_CART_NUM || !item.variantId) return;

        const ext = this.cart.items?.find((x: ItfEcoCartItem) => x.variantId === item.variantId);
        if (ext && ext.quantity > MIN_CART_NUM) {
            ext.quantity -= 1;
            // 减少为0 => 删除
            if (ext.quantity <= MIN_CART_NUM) {
                const extIdx = this.cart.items?.findIndex((x: ItfEcoCartItem) => x.variantId === item.variantId);
                if (extIdx !== -1) {
                    this.cart.items.splice(extIdx, 1);
                }
            }
        }
        this.cart.open = true;
        this.notifySubscribers();

        if (this.cart.isLogin === 1) {
            const productList: ItfEcoCartItemChange[] = [
                {
                    compareAtPrice: item.compareAtPrice,
                    price: item.price,
                    productId: Number(item.productId),
                    variantId: Number(item.variantId),
                    quantity: item.quantity - 1,
                },
            ];
            await SERVICE.API_UPDATE_USER_CART({ productList });
        } else if (this.cart.isLogin === 0) {
            STORAGE.STORAGE_UPDATE_CARTS({ ...item, quantity: item.quantity - 1 });
        }

        handle_GTM_30({
            product_name: item.productTitle || item.skuTitle,
            price: item.price,
            quantity: 1,
            button_name: '-',
            product_id: item.productId,
            variant_id: item.variantId,
        });
    }

    private async handleCartRemove(item: ItfEcoCartItem) {
        console.log('handleCartRemove:', item);
        if (!item.variantId) return;

        handle_GTM_30({
            product_name: item.productTitle || item.skuTitle,
            price: item.price,
            quantity: item.quantity,
            button_name: '+',
            product_id: item.productId,
            variant_id: item.variantId,
        });

        const extIdx = this.cart.items?.findIndex((x: ItfEcoCartItem) => x.variantId === item.variantId);
        if (extIdx !== -1) {
            this.cart.items.splice(extIdx, 1);
        }
        this.cart.open = true;
        this.notifySubscribers();

        if (this.cart.isLogin === 1) {
            const productList: ItfEcoCartItemChange[] = [
                {
                    compareAtPrice: item.compareAtPrice,
                    price: item.price,
                    productId: Number(item.productId),
                    variantId: Number(item.variantId),
                    quantity: 0,
                },
            ];
            await SERVICE.API_UPDATE_USER_CART({ productList });
        } else if (this.cart.isLogin === 0) {
            STORAGE.STORAGE_UPDATE_CARTS({ ...item, quantity: 0 });
        }
    }

    private handleCartIsLogin(n: 0 | 1 | undefined) {
        const isLogin = typeof n === 'number' && [0, 1].includes(n) ? n : getIsLogin() ? 1 : 0;
        this.cart.isLogin = isLogin;
        this.notifySubscribers();
    }

    private async handleCartCoupon(items: ItfEcoCartItem[], isLogin: ItfEcoCartIsLogin, locale: string) {
        if (isLogin !== 1 || items?.length <= 0 || !locale) return;
        const data: ItfCouponGift = {
            source: 'CART',
            productList: items.reduce((arr: ItfCouponGiftProductItem[], item: ItfEcoCartItem) => {
                const { price, quantity, productId, variantId } = item;
                arr.push({ price: `${price}`, quantity, productId, variantId });
                return arr;
            }, [] as ItfCouponGiftProductItem[]),
        };
        const coupon: ItfEcoCartCouponItem = await SERVICE.API_GET_COUPON_GIFT(data, locale);
        this.cart.coupon = coupon;
        this.notifySubscribers();
    }

    private handleCartIcon(pathname: string) {
        this.cart.icon = pathname || '';
        this.notifySubscribers();
    }

    // 订阅状态变化
    subscribe(callback: (cart: ItfEcoCart) => void) {
        this.subscribers.add(callback);
        return () => {
            this.subscribers.delete(callback);
        };
    }

    // 通知所有订阅者
    private notifySubscribers() {
        // 创建深拷贝，避免状态被意外修改
        const cartCopy = {
            open: this.cart.open,
            items: this.cart.items.map((item) => ({ ...item })),
            coupon: this.cart.coupon ? { ...this.cart.coupon } : null,
            isLogin: this.cart.isLogin,
            icon: this.cart.icon,
        };

        this.subscribers.forEach((callback) => {
            try {
                callback(cartCopy);
            } catch (error) {
                console.error('Error in cart subscriber:', error);
            }
        });
    }

    // 获取当前状态
    getCart(): ItfEcoCart {
        return {
            open: this.cart.open,
            items: this.cart.items.map((item) => ({ ...item })),
            coupon: this.cart.coupon ? { ...this.cart.coupon } : null,
            isLogin: this.cart.isLogin,
            icon: this.cart.icon,
        };
    }

    // 销毁（可选）
    destroy() {
        if (!this.isInitialized) return;

        EcoDesignEventsBus.off(ENUM_ECO_CART_EVENT.ECO_CART_OPEN, this.handleCartOpen);
        EcoDesignEventsBus.off(ENUM_ECO_CART_EVENT.ECO_CART_QUERY, this.handleCartQuery);
        EcoDesignEventsBus.off(ENUM_ECO_CART_EVENT.ECO_CART_MERGE, this.handleCartQuery);
        EcoDesignEventsBus.off(ENUM_ECO_CART_EVENT.ECO_CART_ADD, this.handleCartAdd);
        EcoDesignEventsBus.off(ENUM_ECO_CART_EVENT.ECO_CART_INCREASE, this.handleCartIncrease);
        EcoDesignEventsBus.off(ENUM_ECO_CART_EVENT.ECO_CART_DECREASE, this.handleCartDecrease);
        EcoDesignEventsBus.off(ENUM_ECO_CART_EVENT.ECO_CART_REMOVE, this.handleCartRemove);
        EcoDesignEventsBus.off(ENUM_ECO_CART_EVENT.ECO_CART_ISLOGIN, this.handleCartIsLogin);
        EcoDesignEventsBus.off(ENUM_ECO_CART_EVENT.ECO_CART_COUPON, this.handleCartCoupon);
        EcoDesignEventsBus.off(ENUM_ECO_CART_EVENT.ECO_CART_ICON, this.handleCartIcon);

        this.subscribers.clear();
        this.isInitialized = false;

        console.log('EcoCartStore: Destroyed');
    }
}

export default EcoCartGlobalStore;
